import React, { useState, useEffect } from 'react';

import i18next from 'i18next';
import API from '../lib/APIHelpers/API';
import * as Swedish from '../../translations/Swedish.json';
import * as English from '../../translations/English.json';

export const useLanguage = (baseURL) => {
    const [languages, setLanguages] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('preferredLanguage') || 'sv');
    const [i18nInstance, setI18nInstance] = useState(i18next);
    const [isLoading, setIsLoading] = useState(true);

    const fetchLanguages = () => {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await API.get(`${baseURL}/public/languages`);
                if (response[0]) {
                    resolve(response[0]);
                }
            } catch (error) {
                console.error('Failed to fetch languages:', error);
                resolve({ sv: 'Swedish', en: 'English' });
            }

        });

    };

    const fetchTranslations = (language) => {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await API.get(`${baseURL}/public/translations/${language}`);
                resolve(response[0]);
            } catch (error) {
                console.error('Failed to fetch translations:', error);
                resolve(error);
            }
        });
    };

    useEffect(() => {
        setIsLoading(true);
        fetchLanguages().then(fetchedLanguages => {
            const languageOptions = Object.entries(fetchedLanguages).map(([code, name]) => ({ code, name }));
            setLanguages(languageOptions);
            const preferredLanguage = localStorage.getItem('preferredLanguage');
            if (preferredLanguage && languageOptions.some(lang => lang.code === preferredLanguage)) {
                setSelectedLanguage(preferredLanguage);
            } else {
                setSelectedLanguage(languageOptions[0].code || 'sv');
            }
            setIsLoading(false);
        }).catch(error => {
            console.error('Error fetching languages:', error);
            setIsLoading(false);
        });
    }, [baseURL]);

    useEffect(() => {
        if (!selectedLanguage) return;

        let isMounted = true;
        setIsLoading(true);

        fetchTranslations(selectedLanguage).then(translations => {
            const initializeI18n = (data) => {
                const transformedData = data.reduce((acc, item) => {
                    acc[item.code] = item.value;
                    return acc;
                }, {});
                const resources = {
                    [selectedLanguage]: {
                        translation: transformedData || Swedish,
                    },
                };
                const i18nNewInstance = i18next.createInstance();
                i18nNewInstance.init({ resources, lng: selectedLanguage }).then(() => {
                    if (isMounted) {
                        setI18nInstance(i18nNewInstance);
                        setIsLoading(false);
                    }
                });
            };

            initializeI18n(translations);
        }).catch(() => {
            const resources = {
                sv: { translation: Swedish },
            };
            const i18nFallbackInstance = i18next.createInstance();
            i18nFallbackInstance.init({ resources, lng: 'sv' }).then(() => {
                if (isMounted) {
                    setI18nInstance(i18nFallbackInstance);
                    setIsLoading(false);
                }
            });
        });

        return () => {
            isMounted = false;
        };
    }, [selectedLanguage]);

    const handleLanguageChange = async (event) => {
        const newLanguage = event.target.value;
        localStorage.setItem('preferredLanguage', newLanguage);
        setSelectedLanguage(newLanguage);
        setIsLoading(true);
    };

    return {
        languages,
        selectedLanguage,
        i18nInstance,
        isLoading,
        handleLanguageChange,
    };
};